import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Boxes, Pencil, Plus, RefreshCcw, Search } from 'lucide-react';

import { LoadingButton } from '../../components/common/LoadingButton';
import { ModelDialog, ProviderDialog } from '../../components/admin/llm-catalog';
import {
  useLLMModels,
  useLLMProviders,
  useLLMTaskTypes,
  useToggleLLMModelStatus,
  useToggleLLMProviderStatus,
} from '../../services/llm-catalog.service';
import { LLMModel, LLMProvider, LLMTaskTypeOption } from '../../types/admin';
import { modelTaskTypeCodes } from '../../components/admin/llm-catalog/helpers';
import { formatModelPriceLabel } from '../../utils/llmCatalogModelDisplay';
import { formatAdminNumber } from '../../utils/numberFormat';

export const LLMCatalogManagement: React.FC = () => {
  const {
    data: providers = [],
    isLoading: providersLoading,
    isFetching: providersFetching,
    refetch: refetchProviders,
  } = useLLMProviders();
  const [selectedProviderId, setSelectedProviderId] = React.useState<string>('');
  const {
    data: models = [],
    isLoading: modelsLoading,
    isFetching: modelsFetching,
    refetch: refetchModels,
  } = useLLMModels({
    providerPublicId: selectedProviderId || undefined,
  });
  const { data: taskTypes = [] } = useLLMTaskTypes();
  const [taskTypeFilter, setTaskTypeFilter] = React.useState<string>('');
  const [searchText, setSearchText] = React.useState<string>('');
  const filteredModels = React.useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    return models.filter((model) => {
      if (taskTypeFilter && !modelTaskTypeCodes(model).includes(taskTypeFilter)) return false;
      if (!keyword) return true;
      return (
        model.name.toLowerCase().includes(keyword) || model.code.toLowerCase().includes(keyword)
      );
    });
  }, [models, taskTypeFilter, searchText]);
  const toggleProvider = useToggleLLMProviderStatus();
  const toggleModel = useToggleLLMModelStatus();
  const [providerDialogOpen, setProviderDialogOpen] = React.useState(false);
  const [modelDialogOpen, setModelDialogOpen] = React.useState(false);
  const [editingProvider, setEditingProvider] = React.useState<LLMProvider | null>(null);
  const [editingModel, setEditingModel] = React.useState<LLMModel | null>(null);

  const providerColumns: GridColDef[] = [
    { field: 'name', headerName: 'Provider', flex: 1.2, minWidth: 180 },
    { field: 'code', headerName: 'Code', flex: 0.8, minWidth: 120 },
    { field: 'modelCount', headerName: 'Models', width: 90 },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <Switch
          checked={params.row.isActive}
          disabled={
            toggleProvider.isPending && toggleProvider.variables?.publicId === params.row.publicId
          }
          onChange={() =>
            toggleProvider.mutate({
              publicId: params.row.publicId,
              isActive: !params.row.isActive,
            })
          }
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setEditingProvider(params.row);
            setProviderDialogOpen(true);
          }}
        >
          <Pencil size={16} />
        </IconButton>
      ),
    },
  ];

  const modelColumns: GridColDef[] = [
    { field: 'name', headerName: 'Model', flex: 1.2, minWidth: 180 },
    { field: 'code', headerName: 'Code', flex: 1.3, minWidth: 220 },
    {
      field: 'taskTypeCode',
      headerName: 'Task Type',
      flex: 0.9,
      minWidth: 160,
      renderCell: (params) => {
        const taskTypes: LLMTaskTypeOption[] = params.row.taskTypes ?? [];
        if (taskTypes.length === 0)
          return params.row.taskType?.name ?? params.row.taskTypeCode ?? '-';
        return (
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
            {taskTypes.map((taskType, index) => (
              <Chip
                key={taskType.code}
                size="small"
                color={index === 0 ? 'primary' : 'default'}
                label={taskType.name}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'usageScope',
      headerName: 'Scope',
      width: 110,
      renderCell: (params) => <Chip size="small" label={params.value} />,
    },
    {
      field: 'contextWindow',
      headerName: 'Context',
      width: 110,
      valueFormatter: (value) => (value ? formatAdminNumber(value as number) : '-'),
    },
    {
      field: 'totalPricePerMillionTokens',
      headerName: 'Total Price($)',
      width: 120,
      renderCell: (params) => formatModelPriceLabel(params.row as LLMModel),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <Switch
          checked={params.row.isActive}
          disabled={
            toggleModel.isPending && toggleModel.variables?.publicId === params.row.publicId
          }
          onChange={() =>
            toggleModel.mutate({
              publicId: params.row.publicId,
              isActive: !params.row.isActive,
            })
          }
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setEditingModel(params.row);
            setModelDialogOpen(true);
          }}
        >
          <Pencil size={16} />
        </IconButton>
      ),
    },
  ];

  const gridSx = {
    border: 0,
    height: '100%',
    overflow: 'hidden',
    '& .MuiDataGrid-main': {
      minHeight: 0,
      overflow: 'hidden',
    },
    '& .MuiDataGrid-columnHeaders': {
      flexShrink: 0,
    },
    '& .MuiDataGrid-virtualScroller': {
      overflowY: 'auto !important',
    },
    '& .MuiDataGrid-footerContainer': {
      flexShrink: 0,
    },
  };

  return (
    <Box className="flex h-screen min-h-0 flex-col overflow-hidden bg-slate-50 p-6 dark:bg-slate-900">
      <Box className="mb-6 flex shrink-0 flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Box>
          <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
            LLM Catalog
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Manage providers, model metadata, and where each model can be used
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <LoadingButton
            variant="outlined"
            loading={providersFetching || modelsFetching}
            startIcon={<RefreshCcw size={16} />}
            onClick={() => {
              refetchProviders();
              refetchModels();
            }}
          >
            Refresh
          </LoadingButton>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => {
              setEditingProvider(null);
              setProviderDialogOpen(true);
            }}
          >
            Provider
          </Button>
          <Button
            variant="contained"
            startIcon={<Boxes size={16} />}
            onClick={() => {
              setEditingModel(null);
              setModelDialogOpen(true);
            }}
            disabled={providers.length === 0}
          >
            Model
          </Button>
        </Box>
      </Box>

      <Box className="grid min-h-0 flex-1 grid-cols-1 grid-rows-2 gap-6 xl:grid-cols-[minmax(360px,0.9fr)_minmax(560px,1.4fr)] xl:grid-rows-1">
        <Box className="flex min-h-0 flex-col overflow-hidden rounded-lg bg-white shadow-sm dark:bg-slate-800">
          <Box className="flex h-[68px] shrink-0 items-center border-b border-gray-200 px-4 dark:border-slate-700">
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              Providers
            </Typography>
          </Box>
          <Box className="min-h-0 flex-1">
            <DataGrid
              rows={providers}
              columns={providerColumns}
              loading={providersLoading}
              getRowId={(row) => row.publicId}
              onRowClick={(params) => setSelectedProviderId(params.row.publicId)}
              disableRowSelectionOnClick
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 25]}
              sx={gridSx}
            />
          </Box>
        </Box>

        <Box className="flex min-h-0 flex-col overflow-hidden rounded-lg bg-white shadow-sm dark:bg-slate-800">
          <Box className="flex shrink-0 flex-col gap-3 border-b border-gray-200 p-4 dark:border-slate-700">
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              Models
            </Typography>
            <Box className="flex flex-col gap-3 md:flex-row">
              <TextField
                size="small"
                placeholder="Search name or code"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="md:flex-1"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Task Type</InputLabel>
                <Select
                  label="Task Type"
                  value={taskTypeFilter}
                  onChange={(e) => setTaskTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All task types</MenuItem>
                  {taskTypes.map((taskType) => (
                    <MenuItem key={taskType.code} value={taskType.code}>
                      {taskType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Provider</InputLabel>
                <Select
                  label="Provider"
                  value={selectedProviderId}
                  onChange={(e) => setSelectedProviderId(e.target.value)}
                >
                  <MenuItem value="">All providers</MenuItem>
                  {providers.map((provider) => (
                    <MenuItem key={provider.publicId} value={provider.publicId}>
                      {provider.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box className="min-h-0 flex-1">
            <DataGrid
              rows={filteredModels}
              columns={modelColumns}
              loading={modelsLoading}
              getRowId={(row) => row.publicId}
              disableRowSelectionOnClick
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 25]}
              sx={gridSx}
            />
          </Box>
        </Box>
      </Box>

      <ProviderDialog
        open={providerDialogOpen}
        provider={editingProvider}
        onClose={() => setProviderDialogOpen(false)}
      />
      <ModelDialog
        open={modelDialogOpen}
        providers={providers}
        providerPublicId={selectedProviderId}
        model={editingModel}
        onClose={() => setModelDialogOpen(false)}
      />
    </Box>
  );
};
